import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import differenceBy from 'lodash-es/differenceBy';
import { PositionInfo } from '../../classes/user-preferences';
import { Listing } from '../../classes/listing';
import { Subject, Subscription, BehaviorSubject } from 'rxjs';
import { HasSubscriptions } from '../has-subscriptions';
import { Router } from '@angular/router';
import { RemaxMarker } from '../../classes/remax-marker';

import {TranslateService} from '@ngx-translate/core';
import {CommunicationService} from '../../services/communication.service';
import {ListingsService, MAX_LISTINGS_RETURNED} from '../../services/listings.service';
import {PreferencesService} from '../../services/preferences.service';
import {CardPreviewComponent} from '../card-preview/card-preview.component';
import {SCREEN_SIZE, ViewModes, ListingCategory} from '../../classes/enums';
import {PinInfo} from '../../classes/pin-info';
import {CacheService} from '../../services/cache.service';
import {SearchResult} from '../../classes/object-response';
import {Pagination} from '../../classes/pagination';
import {DeviceService} from '../../services/device/device.service';
import {environment} from '../../../environments/environment-wrapper';
import {Helper} from '../../classes/helper';
import {CurrencyPipe} from '@angular/common';
import MapOptions = google.maps.MapOptions;
import LatLngLiteral = google.maps.LatLngLiteral;
import LatLngBounds = google.maps.LatLngBounds;
import MapTypeStyle = google.maps.MapTypeStyle;
import MarkerOptions = google.maps.MarkerOptions;
import MarkerLabel = google.maps.MarkerLabel;
import { debounceTime, distinctUntilChanged, filter, take } from 'rxjs/operators';
import { timer } from 'rxjs';
import { DomManipulationService } from '../../services/dom-manipulation/dom-manipulation.service';
import { LOCALLOGIC_SCRIPT } from '../../classes/dynamicScripts';
import {PersonaType} from '../../classes/enums';

declare var MarkerClusterer: any;
declare var ClusterIcon: any;
declare var Cluster: any;
declare var locallogic: any;

// Convinces Typescript that these third-party objects have additional properties
// https://www.typescriptlang.org/docs/handbook/declaration-merging.html
declare global {
  interface Cluster {
    clusterIcon_: any;
    hasFavourite: boolean;
    numViewed: number;
  }

  namespace google.maps {
    interface Marker {
      listingId: string;
      listingDate: Date;
      isFavourite: boolean;
      isViewed: boolean;
      listPrice: number;
      listPriceFull: string;
      listPriceShort: string;
    }
  }
}

// Set as imagePath of our MarkerClusterer to distinguish ours vs LocalLogic's
const REMAX_CLUSTERER = 'REMAX_CLUSTERER';

const heartIcon = Helper.getStaticPathForResource('/assets/icons/heart/heart_icon_white_full@1x.svg');
const heartIconSelected = Helper.getStaticPathForResource('/assets/icons/heart/heart_icon_red_full@1x.svg');

const _getMarkerHtml = (text: string, showFavIcon: boolean): string => {
  let favHtml = '';
  if (showFavIcon) {
    // TODO: Translations
    favHtml = '<img alt="Favourite" src="' + heartIcon + '" />';
  }

  return favHtml + '<span>' + text + '</span>';
}

const _getMarkerWidth = (text: string, isFavourite: boolean): string => {
  return (20 + (text.length * 5)) + (isFavourite ? 30 : 0) + 'px';
}

// (45-30) / 500
// 45 is the maximum width/height the cluster can be, 30 is the minimum.
// for every listing in the cluster, up to 500, we increase the size of pin by 0.03px
export const RATIO = 0.03;
export const BOUNDS_RANGE: number = 0.7; // increase the search bounds by 70% of what's currently on the screen

@Component({
  selector: 'app-google-map',
  templateUrl: './google-map.component.html',
  styleUrls: ['./google-map.component.scss']
})
export class GoogleMapComponent extends HasSubscriptions implements OnInit, OnDestroy {
  currentShownListingType: ListingCategory;
  showNoListingsPopupSub: Subscription;
  showNoListingsPopup: boolean = false;
  listingData: Array<PinInfo>;
  @Input() topHeightOffset: number;
  @Input() personaType: PersonaType;

  @Input() enableClosePreviewCards: boolean = true;
  @Input() confirmUnfavourite: boolean = false;
  @Input() isDoneFetchingListings: boolean = false;

  @Output() listingsPreviewed = new EventEmitter<Listing[]>();
  @Output() pinClicked = new EventEmitter<{ markers: google.maps.Marker[], selectedListingId?: string }>();
  @Output() listingCardClicked: EventEmitter<Listing> = new EventEmitter();

  @ViewChild(CardPreviewComponent) cardPreviewRef: CardPreviewComponent;

  listingPreviews = new BehaviorSubject<Listing[]>([]);
  visibleMarkers: google.maps.Marker[];
  showListingPreviews: boolean = false;
  initialListingIndex: number = 0;

  currentPhysicalLocationMarker: RemaxMarker;
  showCurrentPhysicalLocationMarker: boolean = false;
  searchLocationMarker: RemaxMarker;
  showSearchLocationMarker: boolean = false;
  isCurrentlyGettingPhysicalLocation: boolean = false;
  locationButtonClassList: DOMTokenList;

  areFiltersEnabled: boolean = false;

  screenSize: SCREEN_SIZE;

  mapStyles: Array<MapTypeStyle> = [{
    featureType: 'poi',
    elementType: 'labels',
    stylers: [
      { visibility: 'off' }
    ]
  }];

  reloadAfterClustering = false;
  isClustering = new BehaviorSubject<boolean>(false);
  private _mapLoaded = new BehaviorSubject<boolean>(false);
  markerClusterer: MarkerClusterer;
  markersAddedOn: number;

  // These control how the map is initialized and initially shown.
  mapOptions: MapOptions = {
    fullscreenControl: false,
    mapTypeControl: false,
    zoomControl: true,
    keyboardShortcuts: false,
    streetViewControl: true,
    styles: this.mapStyles,
    disableDefaultUI: false,
    draggable: true,
    scrollwheel: true,
    minZoom: 3,
    clickableIcons: false,
    center: {
      lat: 43.606009,
      lng: -79.756893
    },
    zoom: 12
  };

  private _pagination: Pagination = new Pagination();
  private _mapMoved = new Subject<{ bounds: LatLngBounds, zoom: number, center: google.maps.LatLng }>();
  private _map: google.maps.Map;
  // This is used to keep track of the largest bounds that the clusterer currently has listings for.
  // If the map is updated to only show something within these bounds, we don't need to do a new search.
  // If the map has a corner that is outside these bounds, then we would do a new search to add those missing listings.
  private _clustererBounds: ClusterBounds;
  // All markers returned from the last search.
  private _listingMarkers: google.maps.Marker[] = [];
  // Keeps track of all markers currently shown on the map (either as single markers or in clusters).
  private _selectedMarkers: google.maps.Marker[] = [];
  // Keeps track of how many clusters are currently shown on the map
  private _numVisibleClusters: number;
  // The currently selected cluster.
  private _selectedCluster: Cluster;
  // Before we auto move the map based on the user position or to their last map positions, we need to know if the system
  // is instead expecting the map to move to a position that has already been set (ie from a search).
  private _hasLocationBeenSet: boolean = false;
  // When a user is located, this stores their location.
  private _mapPhysicalLocation: LatLngLiteral;
  private _lastSearchBounds: google.maps.LatLngBounds;

  private _favouriteListings: string[] = [];
  private _viewedListings: string[] = [];

  private _activeViewMode: ViewModes;
  SCREEN_SIZE = SCREEN_SIZE;

  constructor(private preferencesService: PreferencesService,
    private listingsService: ListingsService,
    private communicationService: CommunicationService,
    private domManipulationService: DomManipulationService,
    private router: Router,
    private translateService: TranslateService,
    private cache: CacheService,
    private deviceService: DeviceService,
    private currencyPipe: CurrencyPipe
  ) {
    super();
  }

  @Input()
  set setListingData(listingData: { pins: PinInfo[], positionInfo: PositionInfo, filterHash: string }) {
    if (listingData) {
      this.listingData = listingData.pins;
      this._mapLoaded.pipe(filter(loaded => !!loaded), take(1)).subscribe(() => {
        this._lastSearchBounds = new google.maps.LatLngBounds(
          { lat: listingData.positionInfo.south, lng: listingData.positionInfo.west },
          { lat: listingData.positionInfo.north, lng: listingData.positionInfo.east },
        );
        if (this._activeViewMode !== ViewModes.GALLERY) {
          // Before we go ahead and add markers to the clusterer, make sure we don't need to reset it first.
          this._updateClustererBounds(listingData.filterHash);
        }
        this._updateListingMarkers();

        // TODO: Is this the best way to do this?
        // We don't want to be triggering clicks on the listing carousel if we are in desktop mode with the gallery beside the map.
        if (this.screenSize <= SCREEN_SIZE.TABLET_PORTRAIT_UP) {
          // Check if we're coming back to the map from a listing and need to show that listing in a carousel.
          const selectedListingId = this.cache.get<string>(this.cache.KeySelectedListingId, true);
          if (selectedListingId) {
            const previouslySelectedListingIds = this.cache.get<any>(this.cache.KeyPreviouslySelectedListingIds);
            if (previouslySelectedListingIds) {
              this.onClickCard(this._listingMarkers.filter((it) => previouslySelectedListingIds.listingIds.indexOf(it.listingId) >= 0), selectedListingId);
            }
          }
        }
      });
    }
  }

  ngOnInit() {
    if (this.deviceService.isMobileDevice()) this.mapOptions.zoomControl = false;

    this.domManipulationService.loadScript(LOCALLOGIC_SCRIPT);

    this.addSub(this.preferencesService.viewModesChanged.subscribe(mode => {
      this._activeViewMode = mode;
      if (mode === ViewModes.MAP && this.listingData) {
        this._updateListingMarkers();
      }
    }));

    this.addSub(this.communicationService.map.highlightMarker.subscribe(data => {
      // Find the cluster that contains the marker, set it to selected
      // Find the previously selected cluster, set it to not selected.
      if (this.markerClusterer) {

        // Unselect a selected cluster.
        this._clearSelectedCluster();

        if (data.action === 'enter') {
          for (const cluster of this.markerClusterer.getClusters()) {
            const markers = cluster.getMarkers();
            for (const marker of markers) {
              if (marker.listingId === data.listingId) {
                this._setSelectedCluster(cluster);
                break;
              }
            }
          }
        }
      }
    }));

    this.addSub(this.preferencesService.favouriteListingsChanged.subscribe((favs: string[]) => {
      const newFavIds = differenceBy(favs, this._favouriteListings);
      const removedFavIds = differenceBy(this._favouriteListings, favs);
      // If this was just an assingment, it was updated immediately before the two compares above could run.
      this._favouriteListings = favs.slice();

      if (this.markerClusterer) {
        // Find the listings on the map and update them and their clusters.
        for (const cluster of this.markerClusterer.getClusters()) {
          const clusterMarkers = cluster.getMarkers();
          // Keep track of how many are in the cluster so we know if we need to remove the fav colour.
          let numFavsInClutser = 0;

          for (const marker of clusterMarkers) {
            const newIndexOf = newFavIds.indexOf(marker.listingId);
            const removeIndexOf = removedFavIds.indexOf(marker.listingId);
            if (newIndexOf > -1) {
              marker.isFavourite = true;
              newFavIds.splice(newIndexOf, 1);
            }
            else if (removeIndexOf > -1) {
              marker.isFavourite = false;
              removedFavIds.splice(newIndexOf, 1);
            }

            if (marker.isFavourite) {
              numFavsInClutser += 1;
            }
          }

          // If there are no favs, or we removed all favs, make sure it's not coloured as fav.
          if (numFavsInClutser === 0) {
            cluster.clusterIcon_.div_.classList.remove('favourite');
            cluster.hasFavourite = false;
          } else {
            cluster.clusterIcon_.div_.classList.add('favourite');
            cluster.hasFavourite = true;
          }

          if (clusterMarkers.length === 1) {
            this._setClusterText(true, cluster, clusterMarkers[0].listPriceShort);
          }

          // If we've exhausted both the new and remove arrays, no need to continue going through clusters.
          if (newFavIds.length === 0 && removedFavIds.length === 0) {
            break;
          }
        }
      }
    }));

    // TODO: ViewedListings will just keep growing and growing. There must be a way to remove them at some point for a user.
    this.addSub(this.preferencesService.viewedListingsChanged.subscribe(viewListings => {
      this._viewedListings = viewListings;
      // For now, all we can do is add a single item.
      if (!this._viewedListings || this._viewedListings.length === 0) return;
      const newViewedId = this._viewedListings[this._viewedListings.length - 1].split(';')[0];

      if (this.markerClusterer) {
        // Find the listing on the map and update it or the cluster.
        const clusters = this.markerClusterer.getClusters();
        for (const cluster of clusters) {
          const clusterMarkers = cluster.getMarkers();
          let markerMatched = false;
          let allViewed = true;

          for (const marker of clusterMarkers) {
            if (marker.listingId === newViewedId) {
              marker.isViewed = true;
              markerMatched = true;
            }

            if (!marker.isViewed) {
              allViewed = false;
            }
          }

          // If there is a non-viewed marker in the cluster, make sure we haven't coloured the cluster as viewed.
          if (!allViewed) {
            cluster.clusterIcon_.div_.classList.remove('viewed');
          } else {
            cluster.clusterIcon_.div_.classList.add('viewed');
          }

          // If we've already found the cluster with the marker we're updating, skip the other clusters.
          if (markerMatched) {
            break;
          }
        }
      }
    }));

    this.addSub(this.communicationService.searching.updateMap.subscribe((update) => {
      if (update && this._map) {
        this.mapOptions.zoom = update.zoom ? update.zoom : Helper.getZoomLevel(update.types);
        this._map.setZoom(this.mapOptions.zoom);

        if (update.location) {
          this._hasLocationBeenSet = true;
          this.mapOptions.center = update.location;
          this._map.setCenter(this.mapOptions.center);
          this.searchLocationMarker = RemaxMarker.generatePlainMarker(false, false, update.location);
          this.showSearchLocationMarker = true;

          if (update.isPhysical) {
            this.currentPhysicalLocationMarker = RemaxMarker.generatePlainMarker(true, false,  update.location);
            this.showCurrentPhysicalLocationMarker = true;
          }
          else if (update.isSearchForLocation) {
            this.searchLocationMarker = RemaxMarker.generatePlainMarker(false, false, update.location);
            this.showSearchLocationMarker = true;
          }
        }

        // TODO: dont like this, but because search is on another page, in the instance we restore a saved search, this component is rebuilt, and
        // it ends up using the last value here which without the below call, would be the last search made if one was made
        this.communicationService.searching.updateMap.next(null);
      } else if (update && !this._map) {
        // map is not initialized, this happens if the user is in gallery mode when the page is loaded
        // we still need to pretend that the map moved to fetch new listings
        this.communicationService.searchParametersChanged.next();
      }
    }));

    this.addSub(this.preferencesService.doneGettingPhysicalLocation.subscribe(status => {
      // Keep track of if we are currently getting a location or not.
      // If we are, don't bother moving the map around yet.
      this.isCurrentlyGettingPhysicalLocation = !status[0];
      if (this.locationButtonClassList) !status[0] ? this.locationButtonClassList.add('active') : this.locationButtonClassList.remove('active');
    }));

    this.addSub(this.preferencesService.filtersChanged.subscribe(filters => {
      this.areFiltersEnabled = Object.keys(filters).some((key) => {
        const filterProp = Object.keys(filters[key]);
        return (key === 'listingTypes' && filterProp.some((z) => filters[key][z] === true)) || (filterProp.length > 0 && key !== 'listingTypes');
      });
    }));

    this.addSub(this._mapMoved.pipe(
      debounceTime(500),
      distinctUntilChanged())
      .subscribe(event => {
        // delay showing the popup when the map is moved for 2 seconds. this is done so we don't immediately display the message while the user is still performing actions
        this.showNoListingsPopup = false;
        if (this.showNoListingsPopupSub) this.showNoListingsPopupSub.unsubscribe();
        this.showNoListingsPopupSub = timer(2000).subscribe(() => {
          this.showNoListingsPopup = true;
        });
        this.preferencesService.setPosition(event.center.lat(), event.center.lng(), event.zoom,
          event.bounds.getSouthWest().lat(), event.bounds.getSouthWest().lng(), event.bounds.getNorthEast().lat(), event.bounds.getNorthEast().lng());

        // We don't need to actually trigger a new search if we are searching within the bounds of the clusterer and it already has
        // listings for where we want to search. Unless the last request has returned 10000 listings (hard limit on the server) in this
        // case we need to fetch again when the user moves the map
        if (!this._isMapBoundsWithinClusterer() || this._isClustererOutOfDate() || this.listingData.length >= MAX_LISTINGS_RETURNED) {
          this.communicationService.searchParametersChanged.next();
        }
        else {
          // Even though we don't need to refresh the listings, we do need to let the desktop gallery know to update the previews.
          this._getAllVisibleMarkers(this.screenSize >= SCREEN_SIZE.TABLET_LANDSCAPE_UP);
          const selectedListingId = this.cache.get<string>(this.cache.KeySelectedListingId, true);
          if (selectedListingId) {
            this.communicationService.map.highlightMarker.next({action: 'enter', listingId: selectedListingId});
          }
        }
      })
    );

    this.addSub(this.communicationService.screenSize.subscribe(s => this.screenSize = s));

    // Reset visible markers once clustering is done so we get the correct listing count.
    this.addSub(this.isClustering.pipe(filter(isClustering => !isClustering)).subscribe(() => {
      this._repaintIfTooManyClusters();
      if (this.reloadAfterClustering) {
        this.reloadAfterClustering = false;
        this._getAllVisibleMarkers(this.screenSize >= SCREEN_SIZE.TABLET_LANDSCAPE_UP);
      }
    }));

    this.addSub(this.communicationService.listings.currentShownListingType.subscribe((mode) => {
      this.currentShownListingType = mode;
    }))
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }
  }

  /**
   * This is a fix for a bug that happens rarely, when we zoom out while the map is clustering and the map
   * is flooded with hundreds of tiny clusters. It may be triggered by rapidly zooming in and out, or sometimes when
   * clicking on a small (2-3) school cluster that doesn't have many listings clustered around it. For the school
   * cluster case, I think the root cause might be a setTimeout() in the default 'clickToZoom' cluster functionality.
   *
   * This method checks if there are more clusters inside the map bounds than there should be, dictated by the grid size
   * (there should be approximately one cluster per 'gridSize' square). If this is exceeded, a repaint is initiated.
   */
  private _repaintIfTooManyClusters() {
    if (this._map && this.markerClusterer) {
      const mapArea = this._map.getDiv().clientWidth * this._map.getDiv().clientHeight;
      const gridArea = Math.pow(this.markerClusterer.getGridSize(), 2);
      const maxClusters = mapArea / gridArea;
      if (this._numVisibleClusters > maxClusters) {
        console.warn('Too many visible clusters for map size. Repainting.', { maxClustersAllowed: maxClusters, visibleClusters: this._numVisibleClusters, totalClusters: this.markerClusterer.getTotalClusters() });
        this._numVisibleClusters = 0;
        this.markerClusterer.repaint();
      }
    }
  }

  private createMapControl(map: google.maps.Map, position: google.maps.ControlPosition, prefix: string): string {
    const id = prefix + '-' + Date.now();
    const localDiv = document.createElement('div');
    localDiv.id = id;

    const controlDiv = document.createElement('div');
    controlDiv.style.zIndex = '1';
    controlDiv['index'] = 1;
    controlDiv.appendChild(localDiv);

    map.controls[position].push(controlDiv);
    return id;
  }

  // Google Maps preferred way to add Map Controls
  // https://developers.google.com/maps/documentation/javascript/controls#CustomControls
  private createMyLocationMapControl(map: google.maps.Map) {
    const locIcon = document.createElement('i');
    locIcon.className = 'my-location-ic material-icons';
    locIcon.innerText = 'my_location';

    const locButton = document.createElement('div');
    locButton.className = 'btn btn-circle btn-maps btn-secondary is-flex has-flex-align-center has-flex-content-center';
    locButton['index'] = 2;
    locButton.appendChild(locIcon);
    locButton.addEventListener('click', () => this.onCenterAtMyLocation());
    this.locationButtonClassList = locButton.classList;

    map.controls[google.maps.ControlPosition.RIGHT_TOP].push(locButton);
  }

  // Enable direct Google Map access so we can keep track of the bounds immediately as they change.
  onMapReady(map: google.maps.Map) {
    this._map = map;

    const localSchoolsId = this.createMapControl(this._map, google.maps.ControlPosition.RIGHT_TOP, 'local-schools');
    this.createMyLocationMapControl(this._map);

    this.addSub(this.domManipulationService.loadScript(LOCALLOGIC_SCRIPT).subscribe(() => {

      const schoolsWidget = new locallogic.LocalSchools(localSchoolsId, {
        googleMapsMap: this._map,
        locale: 'en',
        color: '#417505',
        asMapControl: true,
        filterPanelOpen: this.personaType === PersonaType.MOVING_UP
      });
      if (this.personaType === PersonaType.MOVING_UP) {
        schoolsWidget.enable();
      }

      const heatMapWidget = new locallogic.LocalMaps('heat-map', {
        googleMapsMap: this._map,
        hiddenScores: ['high_schools', 'primary_schools'],
        filterPanelOpen: this.personaType !== undefined
      });

      if (this.personaType !== undefined) {
        heatMapWidget.enable();
      } else {
        heatMapWidget.disable();
      }
      switch (this.personaType) {
        case PersonaType.FIRST_TIME_BUYER_SINGLE:
          heatMapWidget.show(['transit_friendly', 'car_friendly', 'restaurants', 'cafes', 'nightlife', 'vibrant']);
          break;
        case PersonaType.FIRST_TIME_BUYER_COUPLE:
          heatMapWidget.show(['pedestrian_friendly', 'car_friendly', 'groceries', 'cafes', 'nightlife', 'shopping', 'vibrant']);
          break;
        case PersonaType.MOVING_UP:
          heatMapWidget.show(['transit_friendly', 'car_friendly', 'groceries', 'restaurants', 'shopping', 'quiet']);
          break;
        case PersonaType.EMPTY_NESTERS:
          heatMapWidget.show(['transit_friendly', 'car_friendly', 'restaurants', 'cafes', 'nightlife', 'vibrant']);
          break;
        case PersonaType.LUXURY:
          heatMapWidget.show(['pedestrian_friendly', 'car_friendly', 'groceries', 'restaurants', 'cafes', 'shopping', 'quiet']);
          break;
        case PersonaType.MULTI_GENERATIONAL_FAMILY:
          heatMapWidget.show(['pedestrian_friendly', 'car_friendly', 'groceries', 'restaurants', 'shopping', 'quiet']);
          break;
      }
    }));

    // Once the map is ready, move it to the last position it was at (unless we the location has already been set).
    if (!this._hasLocationBeenSet) {
      const position = this.preferencesService.getPosition();
      this.communicationService.searching.updateMap.next({ location: PositionInfo.getCenter(position), zoom: position.zoomLevel });
    }

    const options: MarkerClustererOptions = {
      zoomOnClick: false,
      gridSize: 60,
      minimumClusterSize: 1, // We set this to 1 so that every icon is treated as a cluster, otherwise it will be a google.maps.Marker that can't be css styled.
      calculator: this.markerCalculator,
      imagePath: REMAX_CLUSTERER,
      styles: [],
      clusterClass: 'marker is-flex has-flex-content-center has-flex-align-center'
    };

    this._overrideClusterAddMarker();
    this._overrideClusterIconShow();

    this.markerClusterer = new MarkerClusterer(map, [], options);
    google.maps.event.addListener(this.markerClusterer, 'click', this.onClusterClicked.bind(this));
    google.maps.event.addListener(this.markerClusterer, 'clusteringbegin', () => this.isClustering.next(true));
    google.maps.event.addListener(this.markerClusterer, 'clusteringend', () => this.isClustering.next(false));
    this._mapLoaded.next(true);
  }

  /**
   * Here we are extending MarkerClusterer's implementation of Cluster.addMarker().
   * We extend it to hook into the existing processing of new Markers, and add drawing-details to the cluster based on that Marker.
   * We only run the extension of the original method if it is our instance of MarkererCluster.
   */
  private _overrideClusterAddMarker() {
    if (!Cluster.prototype._originalAddMarker) {
      Cluster.prototype._originalAddMarker = Cluster.prototype.addMarker;
      Cluster.prototype.addMarker = function (marker: google.maps.Marker): boolean {
        const isAdded = this._originalAddMarker(marker);
        if (isAdded && this.markerClusterer_.imagePath_ === REMAX_CLUSTERER) {
          if (marker.isFavourite) {
            this.hasFavourite = true;
          }

          if (marker.isViewed) {
            const numViewed = this.numViewed;
            if (numViewed) {
              this.numViewed = numViewed + 1;
            }
            else {
              this.numViewed = 1;
            }
          }
        }
        return isAdded;
      };
    }
  }

  /**
   * Here we are overriding MarkerClusterer's implementation of ClusterIcon.show() which is responsible for the initial rending
   * of the ClusterIcon. We override because the structure of our icons is fundamentally different than what is allowed by the original.
   * We rename the original method and fallback to it if it is not our instance of MarkererCluster.
   */
  private _overrideClusterIconShow() {
    if (!ClusterIcon.prototype._originalShow) {
      ClusterIcon.prototype._originalShow = ClusterIcon.prototype.show;
      ClusterIcon.prototype.show = function () {
        if (this.cluster_.markerClusterer_.imagePath_ === REMAX_CLUSTERER) {
          if (this.div_) {
            const pos = this.getPosFromLatLng_(this.center_);
            this.div_.style.cssText = this.createCss(pos);
            let showFavIcon = false;

            const clusterSize = this.cluster_.getSize();
            if (clusterSize > 1) {
              this.div_.classList.add('cluster');
              const size = Math.min((clusterSize - 2) * RATIO + 30, 45) + 'px';
              this.div_.style.width = size;
              this.div_.style.height = size;
            } else {
              this.div_.style.width = _getMarkerWidth(this.sums_.text, this.cluster_.hasFavourite);
              this.div_.style.height = '';
            }

            if (this.cluster_.hasFavourite) {
              this.div_.classList.add('favourite');
              if (clusterSize === 1) {
                showFavIcon = true;
              }
            }

            if (this.cluster_.numViewed && this.cluster_.numViewed === clusterSize) {
              this.div_.classList.add('viewed');
            }

            this.div_.innerHTML = _getMarkerHtml(this.sums_.text, showFavIcon);
            this.div_.title = '';
            this.div_.style.display = '';
          }
          this.visible_ = true;
        } else {
          this._originalShow();
        }
      };
    }
  }

  markerCalculator(markers: google.maps.Marker[], numStyles: number) {
    // We're using css styles instead of marker icons, so just return the text.
    let text = '' + markers.length;
    if (markers.length === 1) {
      text = markers[0].getLabel().text;
    }
    return {
      text: text,
      index: 0,
      title: ''
    };
  }

  // Attached to the clusterer to catch cluster clicks.
  onClusterClicked(c: Cluster) {
    this.cache.remove(this.cache.KeySelectedListingId);
    this._clearSelectedCluster();
    this._setSelectedCluster(c, true);

    let markers = c.getMarkers();
    if (markers.length === 1) {

      const listingId = markers[0].listingId;
      if (this.screenSize >= SCREEN_SIZE.TABLET_LANDSCAPE_UP) {
        // When in desktop, we actually want to pass in the list of all visible previews, not just the one that was clicked.
        markers = this._getAllVisibleMarkers();
      }
      this.onClickCard(markers, listingId);
    }
    else {
      this.onClickCard(markers);
    }
  }

  // Called for either a click on a cluster or a marker (which is just a cluster of size 1).
  onClickCard(markers: google.maps.Marker[], selectedListingId?: string) {
    this.communicationService.notifications.notificationChanged.next(null);
    this._selectedMarkers = null; // this is required in order to force angular's change detection to run
    this._selectedMarkers = markers;

    if (this.screenSize >= SCREEN_SIZE.TABLET_LANDSCAPE_UP) {
      this.pinClicked.emit({ markers: markers, selectedListingId: selectedListingId });
    } else {
      this._clickedListingMarker(markers, selectedListingId);
    }
  }

  clearPreviews(): void {
    this.showListingPreviews = false;
    this.listingPreviews.next(null);
    this._clearSelectedCluster();
  }

  onMapClicked(event) {
    this.clearPreviews();
    this.cache.remove(this.cache.KeyPreviouslySelectedListingIds);
    this.cache.remove(this.cache.KeySelectedListingId);
    if (this.screenSize >= SCREEN_SIZE.TABLET_LANDSCAPE_UP) {
      this.pinClicked.emit({ markers: this._getAllVisibleMarkers(), selectedListingId: null });
    }
  }

  onZoomChanged(event) {
    this.clearPreviews();
  }

  onBoundsChanged(event) {
    this._map = event.target;
    // Don't bother doing anything with the map changing if we're in the middle of geolocation which will just move the map anyway.
    if (!this.isCurrentlyGettingPhysicalLocation) {
      if (this._isMapBoundsWithinClusterer()) {
        this._mapMoved.next({ bounds: this._map.getBounds(), zoom: this._map.getZoom(), center: this._map.getCenter() });
      } else {
        const diff1 = Math.abs(this._map.getBounds().getNorthEast().lat() - this._map.getBounds().getSouthWest().lat()) * BOUNDS_RANGE;
        const diff2 = Math.abs(this._map.getBounds().getNorthEast().lng() - this._map.getBounds().getSouthWest().lng()) * BOUNDS_RANGE;
        const searchBounds = new google.maps.LatLngBounds(
          { lat: (this._map.getBounds().getSouthWest().lat() - diff1), lng: (this._map.getBounds().getSouthWest().lng() - diff2) },
          { lat: (this._map.getBounds().getNorthEast().lat() + diff1), lng: (this._map.getBounds().getNorthEast().lng() + diff2) });
        this._mapMoved.next({ bounds: searchBounds, zoom: this._map.getZoom(), center: this._map.getCenter() });
      }
    }
  }

  onListingCardClicked(listing: Listing) {
    // We always start at 0 offset on since we only return elements after the one selected
    this.preferencesService.addNextListings(this.listingPreviews.value.map((l) => ({ listingId: l.listingId, detailUrl: l.detailUrl })));
    this.listingCardClicked.emit(listing);
  }

  onListingCardFocused(listing: { card, index: number }) {
    // TODO: may want to integrate this with the above at some point, in case we want to paginate favourites
    if ((this.listingPreviews.value.length < this._selectedMarkers.length) && listing.index >= this.listingPreviews.value.length - 3) {
      this._getMoreCards().then(response => this._setListingPreviews(response.results, true));
    }
    this.preferencesService.addViewedListing(listing.card.listingId);
  }

  onCenterAtMyLocation() {
    this.preferencesService.centerMapAtCurrentLocation(false, true);
    // TODO: Animations for ellipsisi, can we do thus better in the service
    this.communicationService.notifications.notificationChanged.next(this.translateService.instant('notification.searching-for-location'));
  }

  onShowSaveSearch() {
    this.communicationService.toolbar.saveSearchRequested.next();
  }

  openFilters($event) {
    $event.preventDefault();
    this.communicationService.toolbar.filtersRequested.next();
  }

  async goToListing(listing: Listing) {
    await this.router.navigate([listing.detailUrl]);
  }

  private async _getMoreCards() {
    let startingIndex = this._pagination.offset;

    // Skip the limit check if we're getting the first page of results.
    if (this._pagination.pageNumber !== 0) {
      // make sure we don't get more cards then there are
      if (startingIndex > this._selectedMarkers.length) return;
    }
    else {
      // If we're on the first page, hardcode the startingIndex to 0.
      startingIndex = 0;
    }

    this._pagination.pageNumber++;
    // Paginate the array
    const pageMarkers = this._selectedMarkers.slice(startingIndex, Math.min(startingIndex + this._pagination.limit, this._selectedMarkers.length));
    const keys = pageMarkers.map(m => m.listingId);
    return await this.listingsService.getListingsPreview(keys.join(','));
  }

  // This method is not run when viewing the desktop map with the side gallery.
  private async _clickedListingMarker(markers: google.maps.Marker[], selectedListingId?: string) {
    this._pagination = new Pagination(0, 10);

    // Check if we are expecting to scroll the carousel to a specific listing in the cluster.
    if (selectedListingId) {
      let listingIndex = -1;
      for (let i = 0; i <= markers.length; i++) {
        if (markers[i].listingId === selectedListingId) {
          listingIndex = i;
          break;
        }
      }

      if (listingIndex > -1) {
        // Get enough listings so we can scroll to our previously selected index.
        // Load pages of listing previews until we get to the one containing our desired selected listing.
        // This shouldn't be a performance hit because all of the pages of listing previews should be cached already.
        // We can now scroll the carousel to that listing.
        const listOfPromises: Promise<SearchResult<Listing[]>>[] = [];
        for (let c = 0; c <= listingIndex; c += 10) {
          listOfPromises.push(this._getMoreCards());
        }
        const response: Listing[] = await Promise.all(listOfPromises)
          .then(result => {
            return result.reduce((a, b) => a.concat(b.results), []);
          });
        this.initialListingIndex = listingIndex;
        this._setListingPreviews(response);
      }
    } else {
      const response = await this._getMoreCards();
      // By default, we add the first listing in the listing preview as a viewed listing
      this.preferencesService.addViewedListing(response.results[0].listingId);
      this.initialListingIndex = 0;
      this._setListingPreviews(response.results);
    }
  }

  private _setListingPreviews(newPreviews: Listing[], shouldConcat: boolean = false) {
    newPreviews.forEach((listing) => {
      listing.isFavourite = this._favouriteListings.indexOf(listing.listingId) > -1;
    });

    if (shouldConcat) {
      this.listingPreviews.next(this.listingPreviews.value.concat(newPreviews));
    } else {
      this.listingPreviews.next(newPreviews);
      this.showListingPreviews = true;
    }

    this.listingsPreviewed.emit(newPreviews);
  }

  private _updateListingMarkers() {

    // split all viewed listings here for better performance
    // TODO: Store parsed viewed-listings ids. We aren't using any other properties here.
    const viewedListingIds = this._viewedListings.map((it) => it.split(';')[0]);

    const newMarkers = this.listingData.map(p => {
      const marker = new google.maps.Marker();
      const markerOptions: MarkerOptions = {
        position: { lat: p.lat, lng: p.lng }
      };
      const markerLabel: MarkerLabel = {
        text: '',
        color: 'white',
        fontSize: '14px',
        fontWeight: 'bold',
      };

      // Custom properties
      marker.listingId = p.listingId;
      marker.listingDate = p.listingDate;
      marker.isFavourite = this._favouriteListings.indexOf(p.listingId) > -1;
      marker.isViewed = viewedListingIds.indexOf(p.listingId) >= 0;
      if (this.currentShownListingType === ListingCategory.COMMERCIAL && p.pricePerSqft) {
        const sqFtText = this.translateService.instant('listing-summary.per-sqft');
        marker.listPrice = p.pricePerSqft;
        marker.listPriceFull = this.currencyPipe.transform(p.pricePerSqft, 'CAD', 'symbol-narrow', '1.2-2') + sqFtText;
        marker.listPriceShort = '$' + Helper.formatPrice(p.pricePerSqft) + sqFtText;
      } else {
        marker.listPrice = p.listPrice;
        marker.listPriceFull = this.currencyPipe.transform(p.listPrice, 'CAD', 'symbol-narrow', '1.0-0');
        marker.listPriceShort = '$' + Helper.formatPrice(p.listPrice);
      }
      markerLabel.text = marker.listPriceShort;
      markerOptions.label = markerLabel;
      marker.setOptions(markerOptions);
      return marker;
    });
    this._listingMarkers = newMarkers;

    // No need to show clusters on a map if we're in the gallery view.
    if (this._activeViewMode !== ViewModes.GALLERY) {

      if (!this.markerClusterer) return;
      const currentMarkers = this.markerClusterer.getMarkers();
      const newSetOfMarkers = currentMarkers.length === 0;

      // Find markers in the new list that don't exist in the current list that should be added to the clusterer.
      const markersToAdd = differenceBy(newMarkers, currentMarkers, 'listingId');
      if (markersToAdd.length > 0) {
        this.markerClusterer.addMarkers(markersToAdd);

        // Are we adding markers for the first time? Timestamp it so we know to eventually remove them.
        if (newSetOfMarkers) {
          this.markersAddedOn = Date.now();
        }
      }
    }
    if (!this.isClustering.value) {
      this._getAllVisibleMarkers(this.screenSize >= SCREEN_SIZE.TABLET_LANDSCAPE_UP);
    } else {
      this.reloadAfterClustering = true;
    }
  }

  // Keep track of the bounds that the clusterer currently has markers for.
  private _updateClustererBounds(filterHash: string) {
    if (this._clustererBounds && this._clustererBounds.filterHash === filterHash && !this._isClustererOutOfDate()) {
      // If one of the mapBounds corners isn't in the current clustererBounds, then we've just done a search that is going to add items to
      // the clusterer, so update it's bounds to include the mapBounds.
      const extended = this._clustererBounds.extendIfNotContained(this._lastSearchBounds);
      if (extended) return;
    }
    // If neither map corner is in the current clusterer bounds, then we've jumped the map to a whole new area.
    // Clear out the clusterer and reset the bounds.
    this._resetClusterer();
    this._clustererBounds = new ClusterBounds(filterHash, this._lastSearchBounds);
  }

  // Are the current map bounds fully within the bounds of the clusterer?
  private _isMapBoundsWithinClusterer(): boolean {
    return this._clustererBounds && this._clustererBounds.contains(this._map.getBounds());
  }

  // Returns true if the clusterer was created more than the cache limit time
  private _isClustererOutOfDate(): boolean {
    return !!this.markersAddedOn && (Date.now() - this.markersAddedOn > environment.clustererCacheSeconds * 1000)
  }

  private _clearSelectedCluster() {
    if (this.markerClusterer && this._selectedCluster) {
      if (this._selectedCluster.clusterIcon_.div_) {
        this._selectedCluster.clusterIcon_.div_.classList.remove('selected');
        if (this._selectedCluster.getSize() === 1) {
          this._setClusterText(false, this._selectedCluster, this._selectedCluster.getMarkers()[0].listPriceShort);
        }
      }

      this._selectedCluster = null;
    }
  }

  private _setSelectedCluster(c: Cluster, setCache: boolean = false) {
    if (c.getSize() === 1 && this.screenSize >= SCREEN_SIZE.TABLET_LANDSCAPE_UP) {
      // remove the cache if the user clicks on single listing, we do it for desktop only
      this.cache.remove(this.cache.KeyPreviouslySelectedListingIds);
    } else if (setCache) {
      // add the items to the cache, so we can show them to the user if they navigate back to the home-page
      this.cache.set(this.cache.KeyPreviouslySelectedListingIds, {listingIds: c.getMarkers().map((it) => it.listingId)}, 300);
    }

    if (c.clusterIcon_.div_ && c.clusterIcon_.div_.classList) {
      c.clusterIcon_.div_.classList.add('selected');

      if (c.getSize() === 1) {
        this._setClusterText(true, c, c.getMarkers()[0].listPriceFull);
      }
      this._selectedCluster = c;
    }
  }

  private _setClusterText(isSelected: boolean, cluster: Cluster, text: string) {
    if (isSelected) {
      cluster.clusterIcon_.div_.innerHTML = _getMarkerHtml(text, cluster.hasFavourite);
      cluster.clusterIcon_.div_.style.width = _getMarkerWidth(cluster.clusterIcon_.div_.innerText, cluster.hasFavourite);
      cluster.clusterIcon_.div_.innerHTML = cluster.clusterIcon_.div_.innerHTML.replace(heartIcon, heartIconSelected);
    } else {
      cluster.clusterIcon_.div_.innerHTML = _getMarkerHtml(text, cluster.hasFavourite);
      cluster.clusterIcon_.div_.style.width = _getMarkerWidth(cluster.clusterIcon_.div_.innerText, cluster.hasFavourite);
    }
  }

  private _resetClusterer() {
    this.markersAddedOn = null;
    this._clustererBounds = null;
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }
  }

  // The clusterer can include markers that are slightly off the visible part of the map. This can make the desktop side gallery look weird
  // when zoomed in close since there will be one or two markers on the map, but three or four in the gallery.
  private _getAllVisibleMarkers(notifyGallery?: boolean): google.maps.Marker[] {
    this.visibleMarkers = [];

    // if we're in the gallery view, just display all listingMarkers
    if (this._activeViewMode === ViewModes.GALLERY) {
      this.visibleMarkers = this._listingMarkers;
    } else {
      const clusters = this.markerClusterer.getClusters();
      const mapBounds = this._map.getBounds();

      this._numVisibleClusters = 0;
      for (const cluster of clusters) {
        if (mapBounds.contains(cluster.getCenter())) {
          this.visibleMarkers.push(...cluster.getMarkers());
          this._numVisibleClusters++;
        }
      }
    }
    if (notifyGallery && this.visibleMarkers.length) {
      this.pinClicked.emit({ markers: this.visibleMarkers });
    }
    return this.visibleMarkers;
  }
}

/**
 * Used to track what Map searches we've already received for the existing filterHash.
 * Stores an LatLngBounds[] instead of using LatLngBounds.union() because union() does not do proper polygon unioning.
 */
class ClusterBounds {
  // TODO: Smarter polygon unioning.

  private _bounds: google.maps.LatLngBounds[] = []
  constructor(
    public readonly filterHash: string,
    bound?: google.maps.LatLngBounds
  ) {
    if (bound) this._bounds.push(bound);
  }

  contains(newBounds: google.maps.LatLngBounds): boolean {
    return this._bounds.some(bound => bound.equals(newBounds) ||
      (bound.contains(newBounds.getNorthEast()) && bound.contains(newBounds.getSouthWest())));
  }

  /**
   *
   * @param newBounds
   * @returns True if ClusterBounds already contained newBounds, or was extended to include them.
   *          False if newBounds shared no common corner with the existing bounds
   */
  extendIfNotContained(newBounds: google.maps.LatLngBounds): boolean {
    let commonCorner = false;
    for (const bound of this._bounds) {
      const sw = bound.contains(newBounds.getSouthWest());
      const ne = bound.contains(newBounds.getNorthEast());
      if (sw && ne) {
        return true;
      }
      if (!commonCorner) {
        const nw = bound.contains({ lat: newBounds.getNorthEast().lat(), lng: newBounds.getSouthWest().lng() });
        const se = bound.contains({ lat: newBounds.getSouthWest().lat(), lng: newBounds.getNorthEast().lng() });
        commonCorner = sw || ne || nw || se;
      }
    }
    if (commonCorner) {
      this._bounds.push(newBounds);
    }
    return commonCorner;
  }
}
